import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { runAnalysis, RunAnalysisResponse } from '../../../services/bots-activity/BotsActivityService';
import {
  GET_BOTS_ACTIVITY_CONTENT,
  GET_BOTS_ACTIVITY_OVERVIEW,
  GET_BOTS_ACTIVITY_STATUS,
  GET_BOTS_ACTIVITY_TIMESERIES,
} from '../../query/bots-activity/keys';

export const useRunBotsActivityAnalysis = (projectId: number | null) => {
  const queryClient = useQueryClient();

  const mutation = useMutation<RunAnalysisResponse, unknown, void>({
    mutationFn: () => {
      if (!projectId) throw new Error('projectId is required');
      return runAnalysis(projectId);
    },
    onSuccess: (data) => {
      if (data.status !== 'ok') return;
      queryClient.invalidateQueries({ queryKey: [GET_BOTS_ACTIVITY_OVERVIEW, projectId] });
      queryClient.invalidateQueries({ queryKey: [GET_BOTS_ACTIVITY_TIMESERIES, projectId] });
      queryClient.invalidateQueries({ queryKey: [GET_BOTS_ACTIVITY_CONTENT, projectId] });
      queryClient.invalidateQueries({ queryKey: [GET_BOTS_ACTIVITY_STATUS, projectId] });
    },
  });

  // Surface the server's structured failure as `data` even when the HTTP status was non-2xx
  // (axios throws on 4xx/5xx by default). Callers want a single `data.status` to switch on.
  const errorPayload =
    mutation.error && axios.isAxiosError(mutation.error)
      ? (mutation.error.response?.data as RunAnalysisResponse | undefined)
      : undefined;

  return {
    runAnalysis: () => mutation.mutate(),
    runAnalysisAsync: () => mutation.mutateAsync(),
    data: mutation.data ?? errorPayload,
    isPending: mutation.isPending,
    isError: mutation.isError,
    reset: mutation.reset,
  };
};
